import { CheckOutlined, PlusCircleFilled } from '@ant-design/icons';
import { type ICurrencyType } from '@mahawi/eshop-common/src/types';
import { Button, Form, InputNumber, Select } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import {
  deliveryCreate,
  deliveryUpdate,
} from '../../reducers/delivery/reducer';
import { type IDeliveryRecord } from '../../reducers/delivery/types';

interface IForm {
  currencyTypeIsoCode: string;
  weight: number;
  price: number;
}

interface DeliveryFeeAddProps {
  countryTypeIsoCode: string;
  editingDelivery?: IDeliveryRecord | null;
  onEditSubmitted?: () => void;
}

function DeliveryFeeAdd({
  countryTypeIsoCode,
  editingDelivery = null,
  onEditSubmitted,
}: DeliveryFeeAddProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const currencyCurrencies = useSelector(
    (s: RootState) => s.Currency.currencies,
  );
  const deliveryInProcess = useSelector((s: RootState) => s.Delivery.inProcess);

  const [form] = Form.useForm<IForm>();

  useEffect((): void => {
    if (editingDelivery) {
      form.setFieldsValue({
        currencyTypeIsoCode: editingDelivery.currencyTypeIsoCode,
        weight: editingDelivery.weight,
        price: editingDelivery.price,
      });
    } else {
      form.resetFields();
    }
  }, [editingDelivery, form]);

  const onFinish = useCallback(
    ({ currencyTypeIsoCode, weight, price }: IForm): void => {
      if (editingDelivery) {
        dispatch(
          deliveryUpdate(
            editingDelivery.id,
            countryTypeIsoCode,
            currencyTypeIsoCode,
            weight,
            price,
          ),
        );
        onEditSubmitted?.();
      } else {
        dispatch(
          deliveryCreate(
            countryTypeIsoCode,
            currencyTypeIsoCode,
            weight,
            price,
          ),
        );
      }

      form.resetFields();
    },
    [dispatch, editingDelivery, countryTypeIsoCode, onEditSubmitted, form],
  );

  return (
    <Form form={form} layout="inline" onFinish={onFinish}>
      <Form.Item
        name="currencyTypeIsoCode"
        label="Currency"
        rules={[{ required: true, message: 'Currency is required' }]}
      >
        <Select
          showSearch
          style={{ width: 140 }}
          disabled={deliveryInProcess}
          options={currencyCurrencies.map(
            ({ isoCode, name }: ICurrencyType) => ({
              value: isoCode,
              label: `${isoCode} — ${name}`,
            }),
          )}
        />
      </Form.Item>

      <Form.Item
        name="weight"
        label="Weight (kg)"
        rules={[{ required: true, message: 'Weight is required' }]}
      >
        <InputNumber min={0} disabled={deliveryInProcess} />
      </Form.Item>

      <Form.Item
        name="price"
        label="Price"
        rules={[{ required: true, message: 'Price is required' }]}
      >
        <InputNumber min={0} disabled={deliveryInProcess} />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          icon={editingDelivery ? <CheckOutlined /> : <PlusCircleFilled />}
          disabled={deliveryInProcess}
          loading={deliveryInProcess}
        >
          {editingDelivery ? 'Save' : 'Add'}
        </Button>
      </Form.Item>
    </Form>
  );
}

export default DeliveryFeeAdd;
